import { useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import useLogger from '../../hooks/useLogger';
import { setErrorMessage } from '../../store/errorSlice';
import { RootState } from '../../store/rootReducer';
import Confirm from './Confirm';
import { Modal } from './Modal';

const SESSION_PATHS = ['standby', 'shot', 'chooseframe', 'choosephoto', 'print'];

const SessionHandler: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const lastSegment = location.pathname.split('/').at(-1);
  const isSessionPath = SESSION_PATHS.some((p) => p === lastSegment);
  const subFolder = useSelector((state: RootState) => state.block.block.subFolder);
  const [isOnline, setIsOnline] = useState(true);
  const logger = useLogger();
  const { errorMessage, handleErrorMessageMount, messageDuration, errorContent } = useSelector(
    (state: RootState) => state.error
  );

  // NOTE: 새로고침 시 대화 상자 띄우는 역할
  const handleBeforeUnload = (e: BeforeUnloadEvent) => {
    if (isOnline) {
      e.preventDefault();
      return '';
    }
  };

  const handleOffline = () => {
    flushSync(() => setIsOnline(false));
    dispatch(
      setErrorMessage({
        errorMessage: '네트워크가 불안정해요.',
      })
    );
  };

  // NOTE: 새로고침 대화상자에서 확인 눌렀을 때 실행
  const handleVisibilityChange = () => {
    sessionStorage.setItem('isRefreshed', 'true');
  };

  // NOTE: 뒤로가기, 앞으로가기 시 처리
  const handlePopState = () => {
    const lastSegment = window.location.pathname.split('/').at(-1);
    const isSessionPath = SESSION_PATHS.some((p) => p === lastSegment);

    if (isSessionPath) {
      dispatch(
        setErrorMessage({
          errorMessage: '뒤로가기로 접근할 수 없어요.',
          handleErrorMessageMount: () => {
            logger.errorLog('뒤로가기');
          },
        })
      );
    }
  };

  /*const handleUnknownError = (error: Error) => {
    dispatch(
      setErrorMessage({
        errorMessage: '알 수 없는 오류가 발생했어요.',
        handleErrorMessageMount: () => {
          logger.errorLog(`알 수 없는 오류: ${error.message}`);
        },
      })
    );
  };*/

  useEffect(() => {
    if (isSessionPath) {
      window.addEventListener('beforeunload', handleBeforeUnload);
      window.addEventListener('offline', handleOffline);
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    return () => {
      if (isSessionPath) {
        window.removeEventListener('beforeunload', handleBeforeUnload);
        window.removeEventListener('offline', handleOffline);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }
    };
  }, [isSessionPath, isOnline]);

  useEffect(() => {
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  useEffect(() => {
    const isRefreshed = sessionStorage.getItem('isRefreshed');
    sessionStorage.removeItem('isRefreshed');

    if (isSessionPath) {
      if (isRefreshed) {
        dispatch(
          setErrorMessage({
            errorMessage: '새로고침으로 접근할 수 없어요.',
            handleErrorMessageMount: () => {
              logger.errorLog('새로고침');
            },
          })
        );
      } else {
        dispatch(
          setErrorMessage({
            errorMessage: '직접 진입할 수 없는 화면이에요.',
            handleErrorMessageMount: () => {
              logger.errorLog('세션 보호 페이지에 최초 진입');
            },
          })
        );
      }
    }
  }, []);

  /*useEffect(() => {
    const handleGlobalError = (event: ErrorEvent) => {
      if (event.defaultPrevented) return;

      logger.errorLog(`전역 에러 발생: ${event.message}`);
      handleUnknownError(new Error(event.message));
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (event.defaultPrevented) return;

      logger.errorLog(`Promise 에러 발생: ${event.reason}`);
      handleUnknownError(
        event.reason instanceof Error ? event.reason : new Error(String(event.reason))
      );
    };

    window.addEventListener('error', handleGlobalError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleGlobalError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  });*/

  return (
    <>
      {errorMessage &&
        (messageDuration === null ? (
          <Confirm
            key={errorMessage}
            content={errorContent}
            title="서비스 이용에 문제가 발생했어요"
            errorMessage={errorMessage}
            onMount={handleErrorMessageMount}
          />
        ) : (
          <Modal
            key={errorMessage}
            title="서비스 이용에 문제가 발생했어요"
            time={messageDuration}
            errorMessage={errorMessage}
            onMount={handleErrorMessageMount}
          />
        ))}
      {/** NOTE: beforeunload를 위한 빈 input */}
      <input style={{ display: 'none' }} />
      {children}
    </>
  );
};

export default SessionHandler;
